import { ArrowRight } from "lucide-react";
import { BrutalistBadge, GlassCard, SolidBlock } from "@/app/components/brand-scaffold";
import { Button } from "@/app/components/ui/button";

const glassCardProps = {
  bgClass: "bg-white/75",
  hoverClass: "hover:bg-white/95",
  className: "!p-5 flex flex-col",
} as const;

const glassCardLgProps = {
  bgClass: "bg-white/75",
  hoverClass: "hover:bg-white/95",
  className: "!p-6 flex flex-col",
} as const;

const glassCardStepProps = {
  bgClass: "bg-white/75",
  hoverClass: "hover:bg-white/95",
  className: "!p-4 flex-1 flex flex-col",
} as const;

// --- What Is A Hotline ---
export function HomepageWhatIsHotlineEn() {
  return (
    <section className="px-4 py-16 sm:px-6 lg:px-8 lg:py-20 bg-black/[0.04]">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <BrutalistBadge bgClass="bg-[#A3E635]" hoverBgClass="hover:bg-black" hoverTextClass="hover:text-white">
            WHAT IS A HOTLINE
          </BrutalistBadge>
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-black mb-8">What is a Hotline?</h2>
        <p className="text-lg sm:text-xl font-bold text-zinc-800 mb-8 max-w-3xl">
          A Hotline is an Agent-callable capability entry point. Not a phone line — a standardized capability contract.
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { title: "Capability Description", desc: "What this capability does" },
            { title: "Input Schema", desc: "What inputs are required" },
            { title: "Output Schema", desc: "What outputs are returned" },
            { title: "Risks & Limits", desc: "Boundaries and caveats" },
            { title: "Provider", desc: "Who provides and maintains it" },
            { title: "Version & Audit", desc: "Version + how results are recorded and verified" },
          ].map((item) => (
            <GlassCard key={item.title} {...glassCardProps}>
              <h3 className="font-black text-black mb-1">{item.title}</h3>
              <p className="text-sm text-zinc-700">{item.desc}</p>
            </GlassCard>
          ))}
        </div>
        <div className="mt-8 border-l-8 border-black pl-5">
          <p className="text-base sm:text-lg font-bold text-zinc-800">
            You can expose a Hotline as an MCP Server, OpenAPI endpoint, or Skill wrapper. Those are access surfaces; Hotline is the productized capability contract.
          </p>
        </div>
      </div>
    </section>
  );
}

// --- Call Flow ---
export function HomepageCallFlowEn() {
  const steps = [
    { title: "Agent / Caller initiates request", desc: "Via delexec-ops CLI or SDK, a call is made to the catalog.", bg: "bg-[#14B8A6]" },
    { title: "Supervisor manages session", desc: "Local supervisor handles session, catalog lookup, and call state.", bg: "bg-[#3B82F6] text-white" },
    { title: "Hotline Contract validates", desc: "Defines input, output, version, and risks. Ensures call conforms.", bg: "bg-[#A3E635]" },
    { title: "Responder Adapter executes locally", desc: "Your private implementation runs locally. Core logic never leaves.", bg: "bg-[#F97316] text-white" },
    { title: "result_package returns", desc: "Standardized result returns to Caller with output, duration, status.", bg: "bg-[#8B5CF6] text-white" },
  ];
  return (
    <section className="px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <BrutalistBadge bgClass="bg-[#3B82F6]" hoverBgClass="hover:bg-black" hoverTextClass="hover:text-white" className="text-white">
            HOW A CALL WORKS
          </BrutalistBadge>
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-black mb-10">How does a call happen?</h2>
        <div className="space-y-6">
          {steps.map((step, idx) => (
            <div key={step.title} className="flex gap-4 items-start">
              <div className={`flex-shrink-0 w-12 h-12 flex items-center justify-center border-4 border-black font-black text-lg ${step.bg}`}>{idx + 1}</div>
              <GlassCard {...glassCardStepProps}>
                <h3 className="font-black text-black">{step.title}</h3>
                <p className="text-sm text-zinc-700 mt-1">{step.desc}</p>
              </GlassCard>
            </div>
          ))}
        </div>
        <SolidBlock bgClass="bg-[#A3E635] text-black" className="!p-5 mt-10">
          <p className="font-black text-black">Core principle: Public contract, private implementation.</p>
          <p className="text-sm text-zinc-800 mt-1">Agents see schema and result_package — not your source code, prompts, models, or internal workflows.</p>
        </SolidBlock>
      </div>
    </section>
  );
}

// --- Real Status ---
export function HomepageRealStatusEn() {
  const cards = [
    { num: "01", title: "Local Mode", body: "Install locally and run — no public platform needed.", bgClass: "bg-[#A3E635] text-black", status: "available" as const },
    { num: "02", title: "@delexec/ops CLI", body: "Local runtime for Caller / Responder roles.", bgClass: "bg-[#3B82F6] text-white", status: "available" as const },
    { num: "03", title: "Self-host Platform", body: "Run private catalog, review, and routing for your team.", bgClass: "bg-[#8B5CF6] text-white", status: "available" as const },
    { num: "04", title: "Public Marketplace", body: "Shows example Hotlines to demonstrate catalog and contract structure.", bgClass: "bg-[#FACC15] text-black", status: "preview" as const },
    { num: "05", title: "Fiat Settlement", body: "Fiat settlement and withdrawal — planned.", bgClass: "bg-[#EC4899] text-white", status: "planned" as const },
  ];
  const labels = { available: "Available", preview: "Preview", planned: "Planned" };
  const styles = { available: "bg-black text-white", preview: "bg-white text-black border border-black", planned: "bg-white/80 text-black border border-black/30" };
  return (
    <section className="px-4 py-16 sm:px-6 lg:px-8 lg:py-20 bg-black/[0.04]">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <BrutalistBadge bgClass="bg-white" hoverBgClass="hover:bg-black" hoverTextClass="hover:text-white">REAL STATUS</BrutalistBadge>
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-black mb-4">Current Real Status</h2>
        <p className="text-lg font-bold text-zinc-600 mb-10">We don't present future plans as existing features.</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((card) => (
            <SolidBlock key={card.num} bgClass={card.bgClass} hoverClass="hover:brightness-95" className="!p-5 transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="font-black text-xs opacity-60">{card.num}</span>
                <span className={`px-2 py-0.5 text-[10px] font-black uppercase ${styles[card.status]}`}>{labels[card.status]}</span>
              </div>
              <h3 className="font-black text-lg mb-1">{card.title}</h3>
              <p className="text-sm opacity-90">{card.body}</p>
            </SolidBlock>
          ))}
        </div>
        <p className="mt-8 text-base font-bold text-zinc-800">Recommended first step: complete a local Hotline call.</p>
      </div>
    </section>
  );
}

// --- Pathways ---
export function HomepagePathwaysEn() {
  const pathways = [
    { title: "I have a capability", desc: "Package your local CLI, HTTP service, model inference, or expertise as a Hotline. Run locally first, then decide whether to submit for review.", cta: "Responder Quick Start", href: "/en/docs/quick-start-responder", bgClass: "bg-[#F97316] text-white", textColor: "text-white" },
    { title: "I'm building an Agent", desc: "Let your Agent discover Hotlines via catalog, make calls, and read unified result_packages. No custom client code per capability.", cta: "Caller Quick Start", href: "/en/docs/quick-start-caller", bgClass: "bg-[#14B8A6] text-white", textColor: "text-white" },
    { title: "I want to self-host", desc: "Run private catalog, review queue, relay, and operator console for your team. Suitable for enterprise or research environments.", cta: "Self-host Platform", href: "/en/docs/protocol-architecture", bgClass: "bg-[#FACC15] text-black", textColor: "text-black" },
  ];
  return (
    <section className="px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <BrutalistBadge bgClass="bg-[#F97316]" hoverBgClass="hover:bg-black" hoverTextClass="hover:text-white" className="text-white">GET STARTED</BrutalistBadge>
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-black mb-10">Choose your path</h2>
        <div className="grid gap-6 lg:grid-cols-3">
          {pathways.map((p) => (
            <SolidBlock key={p.title} bgClass={p.bgClass} className="!p-6 flex flex-col">
              <h3 className={`text-xl font-black mb-3 ${p.textColor}`}>{p.title}</h3>
              <p className={`text-sm mb-6 flex-1 ${p.textColor} opacity-90`}>{p.desc}</p>
              <Button asChild variant="secondary" size="sm" className="gap-1 w-fit">
                <a href={p.href}>{p.cta} <ArrowRight className="h-4 w-4" strokeWidth={3} /></a>
              </Button>
            </SolidBlock>
          ))}
        </div>
      </div>
    </section>
  );
}

// --- Why Not Just ---
export function HomepageWhyNotJustEn() {
  const rows = [
    { dimension: "Problem solved", api: "Service interface: how Agents call external services", mcp: "Tool invocation: how Agent runtime invokes tools", skill: "Behavior injection: how Agents get workflow guidance", hotline: "Capability productization: how private capabilities are discovered, versioned, metered, and governed" },
    { dimension: "Provider", api: "Companies / platforms", mcp: "Tool developers", skill: "Skill authors", hotline: "Individuals or small teams (OPCs)" },
    { dimension: "Consumer", api: "App developers", mcp: "Agent runtime", skill: "Agent context", hotline: "Any protocol-conforming Agent" },
    { dimension: "Integration", api: "Caller writes client code", mcp: "Agent runtime starts server", skill: "SKILL.md injected into prompt", hotline: "Both sides follow same contract, no mutual integration" },
    { dimension: "Identity", api: "Implemented by service provider", mcp: "Follows Agent runtime (no protocol standard)", skill: "Usually none", hotline: "Protocol-layer token + introspect" },
    { dimension: "Metering", api: "Provider integrates payment system", mcp: "Usually no built-in metering", skill: "Usually no commercialization mechanism", hotline: "Protocol fields carry pricing / usage / receipts" },
    { dimension: "Discoverability", api: "Self-marketing by provider", mcp: "Bundled with client or manual config", skill: "Copy files locally", hotline: "Marketplace catalog + ratings + versions" },
    { dimension: "Compatibility", api: "Hotline can expose as OpenAPI endpoint", mcp: "Hotline can expose as MCP Server", skill: "Hotline can wrap as Skill", hotline: "Productized capability contract; access surface can be API / MCP / Skill" },
  ];
  return (
    <section className="px-4 py-16 sm:px-6 lg:px-8 lg:py-20 bg-black/[0.04]">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8">
          <BrutalistBadge bgClass="bg-[#8B5CF6]" hoverBgClass="hover:bg-black" hoverTextClass="hover:text-white" className="text-white">COMPLEMENTARY</BrutalistBadge>
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-black mb-4">Why not just API, MCP, or Skills?</h2>
        <p className="text-lg font-bold text-zinc-600 mb-10 max-w-3xl">
          They all have value — they solve different problems. Hotline addresses another layer: when a capability needs to be discovered, called, versioned, metered, and governed, it needs a productized contract.
        </p>
        <div className="grid gap-6 md:grid-cols-2 mb-12">
          <GlassCard {...glassCardLgProps}>
            <span className="text-2xl">🔌</span>
            <h3 className="font-black text-lg mt-2 mb-1">Access Surfaces: API / MCP / Skills</h3>
            <p className="text-sm text-zinc-600 mb-3">Solve "how Agents call tools and services."</p>
            <ul className="space-y-1">
              <li className="text-sm text-zinc-700">• APIs provide stable service interfaces</li>
              <li className="text-sm text-zinc-700">• MCP lets Agents uniformly call tools</li>
              <li className="text-sm text-zinc-700">• Skills inject behavior guidance into models</li>
            </ul>
          </GlassCard>
          <SolidBlock bgClass="bg-[#A3E635] text-black" className="!p-6">
            <span className="text-2xl">📦</span>
            <h3 className="font-black text-lg mt-2 mb-1">Productization Layer: Hotline</h3>
            <p className="text-sm text-zinc-800 mb-3">Solves "how capabilities are discovered, versioned, metered, and governed."</p>
            <ul className="space-y-1">
              <li className="text-sm text-zinc-900">• A Hotline can use API, MCP, or Skill as access surface</li>
              <li className="text-sm text-zinc-900">• CALL ANYTHING standardizes the capability itself</li>
              <li className="text-sm text-zinc-900">• Public contract, private implementation</li>
            </ul>
          </SolidBlock>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-4 border-black text-sm">
            <thead>
              <tr className="bg-black text-white">
                <th className="p-3 text-left font-black">Dimension</th>
                <th className="p-3 text-left font-black">API</th>
                <th className="p-3 text-left font-black">MCP</th>
                <th className="p-3 text-left font-black">Skills</th>
                <th className="p-3 text-left font-black bg-[#A3E635] text-black">Hotline</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={row.dimension} className={idx % 2 === 0 ? "bg-white" : "bg-zinc-50"}>
                  <td className="p-3 font-bold border-r-2 border-black">{row.dimension}</td>
                  <td className="p-3 border-r border-zinc-200">{row.api}</td>
                  <td className="p-3 border-r border-zinc-200">{row.mcp}</td>
                  <td className="p-3 border-r border-zinc-200">{row.skill}</td>
                  <td className="p-3 font-bold bg-[#d9f5a8]">{row.hotline}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

// --- Marketplace Preview ---
export function HomepageMarketplacePreviewEn() {
  const points = [
    { title: "How Hotlines are discovered", desc: "Through catalog search and category browsing." },
    { title: "How contracts are displayed", desc: "Summary, schema, examples, risks." },
    { title: "Responder info", desc: "How provider and version info is organized." },
    { title: "Caller decisions", desc: "How to judge whether to integrate." },
    { title: "Status labels", desc: "Preview / Demo / Coming Soon." },
    { title: "Timeline", desc: "Public publishing, ratings, settlement not yet open." },
  ];
  return (
    <section className="px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <BrutalistBadge bgClass="bg-[#FACC15]" hoverBgClass="hover:bg-black" hoverTextClass="hover:text-white">MARKETPLACE</BrutalistBadge>
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-black mb-4">Marketplace Preview</h2>
        <p className="text-lg font-bold text-zinc-600 mb-8 max-w-3xl">
          The current catalog shows example Hotlines to demonstrate contract structure. Public publishing is not yet open.
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-8">
          {points.map((p) => (
            <GlassCard key={p.title} {...glassCardProps}>
              <h3 className="font-black text-black mb-1">{p.title}</h3>
              <p className="text-sm text-zinc-700">{p.desc}</p>
            </GlassCard>
          ))}
        </div>
        <Button asChild variant="default" size="lg" className="gap-2">
          <a href="/marketplace">Browse example Hotlines <ArrowRight className="h-5 w-5" strokeWidth={3} /></a>
        </Button>
      </div>
    </section>
  );
}

// --- Open Source ---
export function HomepageOpenSourceEn() {
  return (
    <section className="px-4 py-16 sm:px-6 lg:px-8 lg:py-20 bg-black/[0.04]">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <BrutalistBadge bgClass="bg-white" hoverBgClass="hover:bg-black" hoverTextClass="hover:text-white">APACHE-2.0</BrutalistBadge>
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-black mb-8">Open Source Boundary</h2>
        <div className="grid gap-6 md:grid-cols-2">
          <SolidBlock bgClass="bg-[#A3E635] text-black" className="!p-6">
            <h3 className="font-black text-lg mb-4">You can</h3>
            <ul className="space-y-2">
              {["Run the full chain locally", "Package your own capabilities as Hotlines", "Self-host a private catalog", "Build on top of the protocol", "Reproduce the full stack in enterprise or research environments"].map((item) => (
                <li key={item} className="text-sm text-zinc-900 flex items-start gap-2"><span className="font-black">✓</span> {item}</li>
              ))}
            </ul>
          </SolidBlock>
          <SolidBlock bgClass="bg-[#8B5CF6] text-white" className="!p-6">
            <h3 className="font-black text-lg mb-4">Official operated layer</h3>
            <ul className="space-y-2">
              {["Official Marketplace public discovery", "Managed operations and SLA", "Future settlement network and fiat withdrawal", "Public reputation and rating system"].map((item) => (
                <li key={item} className="text-sm text-white/90 flex items-start gap-2"><span className="text-white/60">→</span> {item}</li>
              ))}
            </ul>
          </SolidBlock>
        </div>
      </div>
    </section>
  );
}

// --- FAQ ---
export function HomepageFaqEn() {
  const faqs = [
    { q: "What's the relationship between Hotline and MCP?", lines: ["MCP handles how Agent runtimes call tools — it's the access surface.", "Hotline handles how capabilities are productized — it's the contract layer. A Hotline can expose as an MCP Server, but product / routing / settlement is handled by the Hotline protocol."] },
    { q: "Do I need to deploy a server?", lines: ["No. Local Mode runs the full chain locally: install CLI → start supervisor → mount Hotline → make a call → see result_package.", "When you need public reachability, you can choose Self-host Platform or the future Public Marketplace."] },
    { q: "What's the open source license?", lines: ["CALL ANYTHING's protocol, client runtime, and self-host platform use Apache-2.0.", "You can run locally, package capabilities, self-host a private catalog, or build on the protocol.", "Official Marketplace, public discovery, and future settlement network are operated layers."] },
  ];
  return (
    <section className="px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <BrutalistBadge bgClass="bg-white" hoverBgClass="hover:bg-black" hoverTextClass="hover:text-white">FAQ</BrutalistBadge>
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-black mb-10">Frequently Asked Questions</h2>
        <div className="space-y-6">
          {faqs.map((faq) => (
            <GlassCard key={faq.q} {...glassCardLgProps}>
              <h3 className="font-black text-lg text-black mb-3">{faq.q}</h3>
              <div className="space-y-2">
                {faq.lines.map((line, idx) => (<p key={idx} className="text-sm text-zinc-700">{line}</p>))}
              </div>
            </GlassCard>
          ))}
        </div>
        <a href="/en/faq" className="inline-flex items-center gap-1.5 mt-8 font-black text-sm text-black hover:text-[#3B82F6] transition-colors">
          View all FAQ <ArrowRight className="h-4 w-4" strokeWidth={3} />
        </a>
      </div>
    </section>
  );
}
